import { c as _c } from "react/compiler-runtime";
import { IconButton } from '@repo/design-system/components/ds/button';
import { Icon } from '@repo/design-system/components/ds/icon';
import { Modal } from '@repo/design-system/components/ui/modal';
import { useT } from '@repo/i18n/components/i18n.client';
import { css } from '@repo/panda/css';
import { useCallback, useEffect, useState } from 'react';
import { LazyVideoPlayer as VideoPlayer } from './lazy-video-player';
const lightboxOverlayStyles = css({
  position: 'fixed',
  top: 'none',
  left: 'none',
  width: '[100vw]',
  height: '[100vh]',
  minHeight: '[100dvh]',
  // Use dynamic viewport height on mobile
  backgroundColor: '[rgba(0, 0, 0, 0.9)]',
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
  zIndex: '[9999]',
  cursor: 'pointer',
  overflow: 'hidden'
});
const lightboxContentStyles = css({
  position: 'relative',
  maxWidth: '[90vw]',
  maxHeight: '[90vh]',
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
  cursor: 'default'
});
const mediaStyles = css({
  maxWidth: '[90vw]',
  maxHeight: '[90vh]',
  objectFit: 'contain',
  borderRadius: '8',
  smDown: {
    maxWidth: '[95vw]',
    maxHeight: '[85vh]'
  }
});
const videoPlayerStyles = css({
  width: '[70vw]',
  height: 'auto',
  '& video': {
    borderRadius: '8'
  },
  smDown: {
    width: '[90vw]'
  }
});
const closeButtonStyles = css.raw({
  position: 'absolute',
  top: '20',
  right: '20',
  zIndex: '[10000]',
  smDown: {
    top: '10',
    right: '10'
  }
});
const navigationButtonStyles = css.raw({
  position: 'absolute',
  top: '[50%]',
  transform: 'translateY(-50%)',
  zIndex: '[10000]'
});
const prevButtonStyles = css.raw({
  left: '20',
  smDown: {
    left: '10'
  }
});
const nextButtonStyles = css.raw({
  right: '20',
  smDown: {
    right: '10'
  }
});
export interface MediaItem {
  type: 'image' | 'video';
  src: string;
  alt?: string;
}
interface PinMediaLightboxProps {
  isOpen: boolean;
  onClose: () => void;
  mediaItems: MediaItem[];
  initialIndex?: number;
  title?: string;
}
export const PinMediaLightbox = t0 => {
  const $ = _c(44);
  const {
    isOpen,
    onClose,
    mediaItems,
    initialIndex: t1
  } = t0;
  const initialIndex = t1 === undefined ? 0 : t1;
  let t2;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = ["game_map"];
    $[0] = t2;
  } else {
    t2 = $[0];
  }
  const {
    t
  } = useT(t2);
  const [currentIndex, setCurrentIndex] = useState(initialIndex);
  let t3;
  let t4;
  if ($[1] !== initialIndex) {
    t3 = () => {
      setCurrentIndex(initialIndex);
    };
    t4 = [initialIndex];
    $[1] = initialIndex;
    $[2] = t3;
    $[3] = t4;
  } else {
    t3 = $[2];
    t4 = $[3];
  }
  useEffect(t3, t4);
  let t5;
  if ($[4] !== currentIndex) {
    t5 = () => {
      if (currentIndex > 0) {
        setCurrentIndex(currentIndex - 1);
      }
    };
    $[4] = currentIndex;
    $[5] = t5;
  } else {
    t5 = $[5];
  }
  const handlePrevious = t5;
  let t6;
  if ($[6] !== currentIndex || $[7] !== mediaItems.length) {
    t6 = () => {
      if (currentIndex < mediaItems.length - 1) {
        setCurrentIndex(currentIndex + 1);
      }
    };
    $[6] = currentIndex;
    $[7] = mediaItems.length;
    $[8] = t6;
  } else {
    t6 = $[8];
  }
  const handleNext = t6;
  let t7;
  let t8;
  if ($[9] !== handleNext || $[10] !== handlePrevious || $[11] !== isOpen || $[12] !== onClose) {
    t7 = () => {
      const handleKeyDown = event => {
        if (!isOpen) {
          return;
        }
        bb17: switch (event.key) {
          case "Escape":
            {
              onClose();
              break bb17;
            }
          case "ArrowLeft":
            {
              handlePrevious();
              break bb17;
            }
          case "ArrowRight":
            {
              handleNext();
            }
        }
      };
      document.addEventListener("keydown", handleKeyDown);
      return () => document.removeEventListener("keydown", handleKeyDown);
    };
    t8 = [isOpen, onClose, handlePrevious, handleNext];
    $[9] = handleNext;
    $[10] = handlePrevious;
    $[11] = isOpen;
    $[12] = onClose;
    $[13] = t7;
    $[14] = t8;
  } else {
    t7 = $[13];
    t8 = $[14];
  }
  useEffect(t7, t8);
  let t9;
  if ($[15] !== onClose) {
    t9 = event_0 => {
      if (event_0.target === event_0.currentTarget) {
        onClose();
      }
    };
    $[15] = onClose;
    $[16] = t9;
  } else {
    t9 = $[16];
  }
  const handleOverlayClick = t9;
  if (!isOpen || mediaItems.length === 0) {
    return null;
  }
  const currentMedia = mediaItems[currentIndex];
  if (!currentMedia) {
    return null;
  }
  const showNavigation = mediaItems.length > 1;
  let t10;
  if ($[17] !== t) {
    t10 = t(_temp);
    $[17] = t;
    $[18] = t10;
  } else {
    t10 = $[18];
  }
  let t11;
  let t12;
  if ($[19] === Symbol.for("react.memo_cache_sentinel")) {
    t11 = css(closeButtonStyles);
    t12 = <Icon icon="close" decorative={true} />;
    $[19] = t11;
    $[20] = t12;
  } else {
    t11 = $[19];
    t12 = $[20];
  }
  let t13;
  if ($[21] !== onClose || $[22] !== t10) {
    t13 = <IconButton onClick={onClose} color="secondary" aria-label={t10} className={t11}>{t12}</IconButton>;
    $[21] = onClose;
    $[22] = t10;
    $[23] = t13;
  } else {
    t13 = $[23];
  }
  let t14;
  if ($[24] !== currentMedia.alt || $[25] !== currentMedia.src || $[26] !== currentMedia.type) {
    t14 = <div className={lightboxContentStyles}>{currentMedia.type === "image" ? <img src={currentMedia.src} alt={currentMedia.alt || "Media content"} className={mediaStyles} /> : <VideoPlayer src={currentMedia.src} controls={true} className={videoPlayerStyles} />}</div>;
    $[24] = currentMedia.alt;
    $[25] = currentMedia.src;
    $[26] = currentMedia.type;
    $[27] = t14;
  } else {
    t14 = $[27];
  }
  let t15;
  if ($[28] !== currentIndex || $[29] !== handleNext || $[30] !== handlePrevious || $[31] !== mediaItems.length || $[32] !== showNavigation || $[33] !== t) {
    t15 = showNavigation && <><IconButton onClick={handlePrevious} color="primary" size="lg" disabled={currentIndex === 0} aria-label={t(_temp2)} className={css({
        ...navigationButtonStyles,
        ...prevButtonStyles
      })}><Icon icon="chevron_left" decorative={true} /></IconButton><IconButton onClick={handleNext} color="primary" size="lg" disabled={currentIndex === mediaItems.length - 1} aria-label={t(_temp3)} className={css({
        ...navigationButtonStyles,
        ...nextButtonStyles
      })}><Icon icon="chevron_right" decorative={true} /></IconButton></>;
    $[28] = currentIndex;
    $[29] = handleNext;
    $[30] = handlePrevious;
    $[31] = mediaItems.length;
    $[32] = showNavigation;
    $[33] = t;
    $[34] = t15;
  } else {
    t15 = $[34];
  }
  let t16;
  if ($[35] !== handleOverlayClick || $[36] !== t13 || $[37] !== t14 || $[38] !== t15) {
    t16 = <div className={lightboxOverlayStyles} onClick={handleOverlayClick}>{t13}{t14}{t15}</div>;
    $[35] = handleOverlayClick;
    $[36] = t13;
    $[37] = t14;
    $[38] = t15;
    $[39] = t16;
  } else {
    t16 = $[39];
  }
  let t17;
  if ($[40] !== isOpen || $[41] !== onClose || $[42] !== t16) {
    t17 = <Modal isOpen={isOpen} onOpenChange={onClose} size="full" variant="transparent" hideCloseButton={true}>{t16}</Modal>;
    $[40] = isOpen;
    $[41] = onClose;
    $[42] = t16;
    $[43] = t17;
  } else {
    t17 = $[43];
  }
  return t17;
};
function _temp($) {
  return $.close;
}
function _temp2($_0) {
  return $_0.previous;
}
function _temp3($_1) {
  return $_1.next;
}